import React, { FC, useRef } from 'react';
import { Text, Flex, useDisclosure, Box, Input } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUpload,
  faSpinner,
  faTrash
} from '@fortawesome/pro-light-svg-icons';
import { useFormContext } from 'react-hook-form';
import { ButtonText, FormFeedback } from '@morressier/alembic';
import { FileErrorModal } from './FileErrorModal';
import { IFileUploaded } from './common/types';
import { useDraftResponse } from 'src/contexts/uploadDraftProvider';
import { get } from 'lodash';
import { useCommandResponse } from 'src/contexts/SourceFileCommandResponseContex';
export function wrapLongText(text: string, maxLength: number): string {
  const regex = new RegExp(`.{1,${maxLength}}`, 'g');
  const firstWord = text.split(' ')[0] || '';

  return firstWord.length > maxLength
    ? (text.match(regex) ?? []).join('\n')
    : text;
}

//TODO: Review this component to refactor and remove commented code and unused props.
export const FilesUploadedCards: FC<{
  files: IFileUploaded[];
  handleRemove: (index: number, fileId?: string) => void;
  uploadFile: (file: File, index: number) => Promise<void>;
  field: string;
  submissionId?: string;
}> = props => {
  const { files, handleRemove, uploadFile, field } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    register,
    formState: { errors }
  } = useFormContext<{ [key: string]: IFileUploaded[] }>(); // Access react-hook-form context
  const mainError = get(errors, field);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const filesUpload = event.target.files;
    if (!filesUpload) return;

    for (const file of Array.from(filesUpload)) {
      const newIndex = filesUpload.length; // index of just-appended item
      await uploadFile(file, newIndex);
    }
    // Reset the input value so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const draftContext = useDraftResponse();
  const commandContext = useCommandResponse();
  const responseList =
    draftContext?.draftResponse || commandContext?.commandResponse;

  const deleteUploadedFile = (index: number, file: IFileUploaded) => {
    // Remove all files from the clicked index onward
    if (files.some(file => file.extension === 'tex')) {
      for (let i = files.length - 1; i >= index; i--) {
        handleRemove(i, files[i]?.fileId);
      }
      fileInputRef.current!.value = '';
    } else {
      handleRemove(index, file.fileId);
    }
  };
  const disableNextButton = () => {
    if (files.length > 0) {
      return files.some(file => !file.url);
    }
    return false;
  };
  const isDisableNextButton = disableNextButton();

  const disableDeleteButton = () => {
    if (files.length > 0) {
      return files.some(file => file.isUploading);
    }
    return false;
  };

  const isDeleteButtonDisabled = disableDeleteButton()

  return (
    <>
      <FileErrorModal isOpen={isOpen} onClose={onClose} fileList={files} />
      {files.map((file, index) => (
        <Flex
          key={index}
          border="1px solid"
          borderColor="neutralPrimaryBorder"
          bgColor="surfacePrimaryFill"
          padding={3}
          borderBottomWidth={index === 0 && files.length === 2 ? 0 : 1}
          justifyContent="center"
          alignItems="center"
        >
          <Flex flex="1" textAlign="left">
            <Text textStyle="textBody" color="neutralSecondaryContent">
              {wrapLongText(file.name, 40)}
            </Text>
          </Flex>
          {file.name.endsWith('.tex') && (
            <Flex>
              <Input
                type="file"
                hidden
                accept=".zip"
                {...register(field)}
                onChange={handleFileInputChange}
                ref={fileInputRef}
              />
              <ButtonText
                size="regular"
                colorScheme="blue"
                disabled={files.some(item => item.extension === 'zip')}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  fileInputRef.current?.click();
                }}
                iconLeft={<FontAwesomeIcon icon={faCloudUpload} />}
              >
                Add supporting .zip
              </ButtonText>
            </Flex>
          )}
          {file?.isUploading ? (
            <Text textStyle="textContext">Uploading... {file?.progress}%</Text>
          ) : (
            !file.isUploading && (
              <>
                {mainError?.[index] && (
                  <FormFeedback variant="error">
                    {mainError?.[index]?.message}
                  </FormFeedback>
                )}
                {file.extension !== 'pdf' && (
                  <ButtonText
                    iconLeft={<FontAwesomeIcon icon={faTrash} />}
                    size="regular"
                    variant="caution"
                    onClick={e => {
                      e.stopPropagation(); // Prevent accordion toggle on delete
                      deleteUploadedFile(index, file);
                    }}
                    isDisabled={
                      isDeleteButtonDisabled && (isDisableNextButton || Boolean(mainError?.[index]?.message))
                    }
                    disabled={
                      isDeleteButtonDisabled && (isDisableNextButton || Boolean(mainError?.[index]?.message))
                    }
                  >
                    {''}
                  </ButtonText>
                )}
              </>
            )
          )}
        </Flex>
      ))}
      {files.some(item => item.extension === 'tex') && (
        <Box marginTop={2}>
          {responseList?.status === 'pending' && (
            <Flex alignItems="center" textAlign="center" gap={2}>
              <FontAwesomeIcon icon={faSpinner} spin />
              <Text>Validating files</Text>
            </Flex>
          )}
          {responseList?.status === 'success' && (
            <FormFeedback variant="success">
              LaTex files successfully validated
            </FormFeedback>
          )}
          {responseList?.status === 'error' && (
            <Flex gap={2}>
              <FormFeedback variant="error">Validation failed</FormFeedback>
              <ButtonText onClick={onOpen}>View Issues</ButtonText>
            </Flex>
          )}
        </Box>
      )}
    </>
  );
};
